/*
 * trial-print-variants.js: keeps Template A's stage poster on the colour the shopper picked.
 *
 * Horizon repaints product media by swapping the whole <media-gallery> out of the page it fetches for the
 * new variant. Template A has no media gallery: the toy lives in our own stage block, whose canvas host
 * carries data-skip-subtree-update, so nothing Horizon does reaches the poster. The picker still fires
 * shopify:product:select the moment a pill is chosen, so we listen for that and repoint the <img> from a
 * map of variant id to image that sections/trial-print-stage.liquid prints next to the picker.
 *
 * Price, the hidden variant input and the Add to cart button are Horizon's own and are left alone.
 */

const MAP_SELECTOR = 'script[type="application/json"][data-trial-poster-map]';
const POSTER_SELECTOR = 'img.trial-stage__poster';

/** @type {WeakMap<Element, Record<string, { src: string, srcset?: string }>>} */
const maps = new WeakMap();

/**
 * Reads (once per section) the variant-id to image map printed beside the picker.
 * @param {Element} section
 * @returns {Record<string, { src: string, srcset?: string }> | null}
 */
function posterMap(section) {
  const cached = maps.get(section);
  if (cached) return cached;

  const script = section.querySelector(MAP_SELECTOR);
  if (!script) return null;

  try {
    const parsed = JSON.parse(script.textContent ?? '{}');
    maps.set(section, parsed);
    return parsed;
  } catch (error) {
    console.warn('[trial-print-variants] poster map is not valid JSON', error);
    return null;
  }
}

/**
 * @param {Event & { detail?: { variantId?: string | number }, promise?: Promise<unknown> }} event
 */
function onProductSelect(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  // A card's own picker (quick add, recommendations) must never move the hero poster.
  if (target.closest('product-card')) return;

  const section = target.closest('.shopify-section');
  const stage = section?.querySelector('.trial-pdp--print');
  if (!section || !stage) return;

  // Horizon's fetch rejects when a newer pick supersedes it; an unhandled rejection would show in console.
  event.promise?.catch?.((error) => {
    if (error?.name !== 'AbortError') console.warn('[trial-print-variants]', error);
  });

  const id = String(event.detail?.variantId ?? '').split('/').pop();
  if (!id) return;

  const entry = posterMap(section)?.[id];
  const poster = stage.querySelector(POSTER_SELECTOR);
  if (!entry || !(poster instanceof HTMLImageElement)) return;
  if (poster.dataset.trialVariant === id) return;

  if (entry.srcset) poster.srcset = entry.srcset;
  else poster.removeAttribute('srcset');
  poster.src = entry.src;
  poster.dataset.trialVariant = id;
}

// One document listener for the page: the event bubbles out of <variant-picker>, and the module body runs
// once however many times the tag is on the page.
document.addEventListener('shopify:product:select', onProductSelect);
